import gsap from 'gsap'
import * as THREE from 'three'
import store from '@/store'
import { isMobile, lerp } from '@/scripts/utils'

// const THREE = window.THREE
const vertexShader = require('./shaders/vertex.vert.glsl')
const fragmentShader = require('./shaders/fragment.frag.glsl')

class SingleHand {
  constructor(scene, params) {
    this.params = params
    this.scene = scene
    this.xFromCenter = 0
    this.yFromCenter = 0
  }

  createHand(handNumber) {
    const handUrl = `/static/images/hand/hand-${handNumber}.png`
    const displaceUrl = `/static/images/hand/displace-${handNumber}.jpg`
    const facePromise = new Promise(resolve => new THREE.TextureLoader().load(handUrl, resolve))
    const displacePromise = new Promise(resolve => new THREE.TextureLoader().load(displaceUrl, resolve))

    return Promise.all([facePromise, displacePromise]).then((textures) => {
      this.faceTexture = textures[0]
      this.faceTextureDisplacement = textures[1]
      const w = this.faceTexture.image.naturalWidth
      const h = this.faceTexture.image.naturalHeight
      this.faceTexture.repeat.set(1, 1)

      this.faceTextureDisplacement.wrapS = THREE.RepeatWrapping
      this.faceTextureDisplacement.wrapT = THREE.RepeatWrapping
      this.faceTextureDisplacement.repeat.set(1, 1)

      const fpGeometry = new THREE.PlaneBufferGeometry(w, h)
      this.material = new THREE.RawShaderMaterial({
        transparent: true,
        uniforms: {
          u_image: { value: this.faceTexture },
          u_imageDisplace: { value: this.faceTextureDisplacement },
          mouseX: { value: 0 },
          mouseY: { value: 0 },
          uTime: { value: 0 },
          opacity: { value: 1.0 }
        },
        vertexShader,
        fragmentShader
      })

      this.fp = new THREE.Mesh(fpGeometry, this.material)
      this.fp.position.y = isMobile() ? -this.params.geometryYMobile : -this.params.geometryY
      this.fp.position.z = 0
      this.scene.add(this.fp)
    })
  }

  enter() {
    return new Promise((resolve) => {
      gsap.fromTo('[data-hero-image]', 0.8, {
        autoAlpha: 0,
        scale: 1.2
      }, {
        autoAlpha: 1,
        scale: 1,
        onComplete: resolve
      }, 'start')
    })
  }
}

class Hand {
  constructor(ctx, params) {
    this.params = params
    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 2000)
    this.textureLooaded = false

    const light = new THREE.AmbientLight(0xf7f7f7) // soft white light
    this.scene.add(light)

    this.renderer = new THREE.WebGLRenderer({ alpha: true })
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    ctx.appendChild(this.renderer.domElement)

    this.mouseMoveBind = null
    this.deviceMoveBind = null
    this.mouse = { }
    this.mouse.x = window.innerWidth / 2
    this.mouse.y = window.innerHeight / 2
    this.started = false
    this.currentHand = store.getters.currentHand(store.state.route.name)

    this.hands = []
    const h = new SingleHand(this.scene, this.params)
    this.hands.push(h)
    this.hands[0].createHand(this.currentHand).then(this.bindAndStart.bind(this))
  }

  bindAndStart() {
    if (this.started) return
    this.started = true
    const cameraPos = isMobile() ? this.params.cameraZMobile : this.params.cameraZ
    this.camera.position.set(0, 0, cameraPos)

    this.update()
    this.hands[0].enter().then(() => {
      this.mouseMoveBind = this.handleMove.bind(this)
      this.deviceMoveBind = this.handleMove.bind(this)
      document.body.addEventListener('mousemove', this.mouseMoveBind)
      window.addEventListener('deviceorientation', this.deviceMoveBind)
    })
  }

  resize() {
    const cameraPos = isMobile() ? this.params.cameraZMobile : this.params.cameraZ
    this.camera.position.set(0, 0, cameraPos)
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()

    this.renderer.setSize(window.innerWidth, window.innerHeight)
  }

  update() {
    this.hands.forEach((hand) => {
      const h = hand
      if (h.material) {
        h.material.uniforms.mouseX.value = -hand.xFromCenter / this.params.parallaxFactorX
        h.material.uniforms.mouseY.value = -hand.yFromCenter / this.params.parallaxFactorY
      }
    })

    this.renderer.render(this.scene, this.camera)
  }

  handleMove(event) {
    if (event.type === 'mousemove') {
      this.mouse.x = lerp(event.clientX, this.mouse.x, 0.9)
      this.mouse.y = lerp(event.clientY, this.mouse.y, 0.9)

      const wH = window.innerWidth / 2
      const hH = window.innerHeight / 2

      this.hands.forEach((hand) => {
        const h = hand
        h.xFromCenter = (this.mouse.x - wH) / wH
        h.yFromCenter = (this.mouse.y - hH) / hH
      })
    } else {
      const gamma = event.gamma
      const beta = 54 - event.beta
      this.hands.forEach((hand) => {
        const h = hand
        h.xFromCenter = -gamma * 0.03
        h.yFromCenter = -beta * 0.02
      })
    }

    this.camera.lookAt(0, 0, 0)
    this.camera.updateProjectionMatrix()
  }

  slide(direction = 'left', currentHand = 1, fromMenu = false) {
    if (currentHand <= 0) return
    document.body.removeEventListener('mousemove', this.mouseMoveBind)
    window.removeEventListener('deviceorientation', this.deviceMoveBind)
    const halfView = window.innerWidth / 2
    const visibleHand = this.hands[0]
    this.currentHand = currentHand
    const offset = 512
    const outNext = direction === 'right' ? ((halfView) + offset) : (((halfView) + offset) * -1)
    const outOld = outNext * -1
    const rotationNext = direction === 'right' ? 5 : -5
    const rotationOld = rotationNext * -1
    const newHand = new SingleHand(this.scene, this.params)
    this.hands.push(newHand)
    newHand.createHand(this.currentHand).then(() => {
      const tl = new gsap.TimelineMax({
        delay: fromMenu ? 1 : 0
      })
      tl.addLabel('start')
        .set(newHand.fp.position, { x: outNext })
        .set(newHand, { xFromCenter: rotationNext })
        .set(newHand.material.uniforms.opacity, { value: 0.2 })
        .to(visibleHand.fp.position, 1.8, {
          x: outOld,
          ease: gsap.Expo.easeInOut
        }, 'start')
        .to(visibleHand, 1.8, {
          xFromCenter: rotationOld,
          ease: gsap.Expo.easeInOut
        }, 'start')
        .to(visibleHand.material.uniforms.opacity, 1.6, {
          value: 0.2,
          ease: gsap.Expo.easeInOut
        }, 'start+=0.2')
        .to(newHand.material.uniforms.opacity, 1.6, {
          value: 1,
          ease: gsap.Expo.easeInOut
        }, 'start')
        .to(newHand.fp.position, 1.8, {
          x: 0,
          ease: gsap.Expo.easeInOut
        }, 'start')
        .to(newHand, 1.8, {
          xFromCenter: 0,
          ease: gsap.Expo.easeInOut
        }, 'start')
      tl.eventCallback('onComplete', () => {
        this.scene.remove(this.hands[0].fp)
        this.hands.splice(0, 1)
        document.body.addEventListener('mousemove', this.mouseMoveBind)
        window.addEventListener('deviceorientation', this.deviceMoveBind)
      })
    })
  }
}

export default Hand



// WEBPACK FOOTER //
// ./src/scripts/hand.js